import React, { useState } from 'react';
import { WizardProgress } from '../components/wizard/WizardProgress';
import { WizardStep1Setup } from '../components/wizard/WizardStep1Setup';
import { WizardStep2Schedule } from '../components/wizard/WizardStep2Schedule';
import { WizardStep3Access } from '../components/wizard/WizardStep3Access';
import { WizardStep4Products } from '../components/wizard/WizardStep4Products';
import { WizardStep5Checkout } from '../components/wizard/WizardStep5Checkout';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { useNavigate } from 'react-router-dom';
import { useWizard, WizardProvider } from '../hooks/useWizard';
import { CheckCircle } from 'lucide-react';
function PopUpStoreNewContent() {
  const navigate = useNavigate();
  const {
    data,
    resetWizard
  } = useWizard();
  const [currentStep, setCurrentStep] = useState(1);
  const [errors, setErrors] = useState<string[]>([]);
  const [showSuccess, setShowSuccess] = useState(false);
  const steps = ['Setup', 'Schedule', 'Access', 'Products', 'Checkout'];
  const validateStep = (step: number): string[] => {
    const validationErrors: string[] = [];
    switch (step) {
      case 1:
        if (!data.step1.storeName.trim()) validationErrors.push('Store name is required');
        if (!data.step1.organizationId) validationErrors.push('Organization is required');
        if (!data.step1.slug.trim()) validationErrors.push('URL slug is required');
        if (!data.step1.supportEmail.trim()) validationErrors.push('Support email is required');
        break;
      case 2:
        if (data.step2.scheduleEnabled) {
          if (!data.step2.openDate) validationErrors.push('Open date is required when schedule is enabled');
          if (!data.step2.closeDate) validationErrors.push('Close date is required when schedule is enabled');
          if (data.step2.openDate && data.step2.closeDate) {
            const openDateTime = new Date(`${data.step2.openDate}T${data.step2.openTime}`);
            const closeDateTime = new Date(`${data.step2.closeDate}T${data.step2.closeTime}`);
            if (closeDateTime <= openDateTime) {
              validationErrors.push('Close date must be after open date');
            }
          }
        }
        break;
      case 3:
        if (data.step3.accessType === 'password' && !data.step3.password.trim()) {
          validationErrors.push('Password is required for password-protected stores');
        }
        if (data.step3.accessType === 'code' && !data.step3.accessCode.trim()) {
          validationErrors.push('Access code is required');
        }
        break;
      case 4:
        // Products are optional, but warn if none added
        if (data.step4.products.length === 0) {
          validationErrors.push('Consider adding at least one product');
        }
        break;
      case 5:
        if ((data.step5.fulfillmentMode === 'bulk-ship' || data.step5.fulfillmentMode === 'hybrid') && !data.step5.bulkShippingAddress.trim()) {
          validationErrors.push('Bulk shipping address is required for this fulfillment mode');
        }
        break;
    }
    return validationErrors;
  };
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <WizardStep1Setup data-id="element-1109" />;
      case 2:
        return <WizardStep2Schedule data-id="element-1110" />;
      case 3:
        return <WizardStep3Access data-id="element-1111" />;
      case 4:
        return <WizardStep4Products data-id="element-1112" />;
      case 5:
        return <WizardStep5Checkout data-id="element-1113" />;
      default:
        return <WizardStep1Setup data-id="element-1114" />;
    }
  };
  const handleNext = () => {
    const validationErrors = validateStep(currentStep);
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors([]);
    if (currentStep < 5) {
      setCurrentStep(currentStep + 1);
    } else {
      handleCreateStore();
    }
  };
  const handleBack = () => {
    if (currentStep > 1) {
      setErrors([]);
      setCurrentStep(currentStep - 1);
    }
  };
  const handleCreateStore = () => {
    // In a real app, this would POST to an API
    console.log('Creating store with data:', data);
    // Show success modal
    setShowSuccess(true);
    // Reset wizard and navigate after delay
    setTimeout(() => {
      resetWizard();
      navigate('/pop-up-stores');
    }, 2000);
  };
  return <div className="space-y-8 pb-12" data-id="element-1115">
      <div data-id="element-1116">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-1117">
          Create New Pop-Up Store
        </h1>
        <p className="text-gray-500" data-id="element-1118">
          Follow the steps to configure and publish a new store.
        </p>
      </div>

      <div className="py-4" data-id="element-1119">
        <WizardProgress currentStep={currentStep} steps={steps} data-id="element-1120" />
      </div>

      {errors.length > 0 && <div className="max-w-3xl mx-auto bg-red-50 border border-red-200 rounded-lg p-4" data-id="element-1121">
          <h4 className="text-sm font-medium text-red-800 mb-2" data-id="element-1122">
            Please fix the following errors:
          </h4>
          <ul className="list-disc list-inside text-sm text-red-700 space-y-1" data-id="element-1123">
            {errors.map((error, idx) => <li key={idx} data-id="element-1124">{error}</li>)}
          </ul>
        </div>}

      <div className="max-w-3xl mx-auto" data-id="element-1125">
        {renderStep()}

        <div className="mt-8 flex justify-between" data-id="element-1126">
          <Button variant="secondary" onClick={handleBack} disabled={currentStep === 1} data-id="element-1127">
            Back
          </Button>
          <Button onClick={handleNext} data-id="element-1128">
            {currentStep === 5 ? 'Create Store' : 'Next Step'}
          </Button>
        </div>
      </div>

      {/* Success Modal */}
      <Modal isOpen={showSuccess} onClose={() => {}} title="Store Created Successfully!" size="sm" data-id="element-1129">
        <div className="text-center py-6" data-id="element-1130">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4" data-id="element-1131">
            <CheckCircle className="h-8 w-8 text-green-600" data-id="element-1132" />
          </div>
          <p className="text-gray-600" data-id="element-1133">
            Your pop-up store has been created and is ready to go!
          </p>
        </div>
      </Modal>
    </div>;
}
export function PopUpStoreNew() {
  return <WizardProvider data-id="element-1134">
      <PopUpStoreNewContent data-id="element-1135" />
    </WizardProvider>;
}